import {
  EngagementVotingMappingRecord,
  InteractionClass,
  LinkageType,
  SequenceFlow,
} from "../models/engagementVotingMappingModels";

/**
 * Mock data for engagement-voting mapping records
 * This data is based on the provided CSV records
 */
export const MOCK_ENGAGEMENT_VOTING_MAPPINGS: EngagementVotingMappingRecord[] =
  [
    {
      compass_cid: "TS31554624",
      company_name: "TOTALENERGIES SE",
      voting_record_id:
        "16335433-05_01_2024-TS31554624-rbc-rbc_sustainable_equities-against",
      engagement_record_id:
        "TS31554624-corporate_objective-ghg_emissions_reduction-climate_targets_aligned_to_1_5_degrees-14_04_2021-rbc",
      confidence_score: 0.9,
      rationale:
        "RBC voted against the advisory proposal on the company's sustainability and climate transition plan, citing concerns about alignment with 1.5C targets and stalled engagement. This directly aligns with their engagement objective to achieve climate targets aligned to 1.5 degrees. The engagement record explicitly mentions that a vote was cast AGAINST the company's annual Sustainability and Climate Transition progress report due to stalled engagement with the company, confirming the escalation path. The timing of the vote (2024-05-01) follows the engagement start date (2021-04-14), indicating that the engagement preceded the vote.",
      is_cross_manager: false,
      matching_factors:
        "GHG emissions reduction|Climate targets aligned to 1.5 degrees|Stalled engagement",
      interaction_class: InteractionClass.CONSISTENT_REINFORCEMENT,
      interaction_class_rationale:
        "The same manager (RBC) is involved in both the engagement and the voting decision. The vote against the climate transition plan is consistent with the engagement objective of achieving climate targets aligned to 1.5 degrees, indicating a reinforcement of the engagement goals.",
      linkage_type: LinkageType.ESCALATION_PATH,
      linkage_type_rationale:
        "The vote against the management proposal is explicitly stated as an escalation measure due to stalled engagement, demonstrating a clear cause-and-effect relationship between the engagement and the voting decision.",
      sequence_flow: SequenceFlow.ENGAGEMENT_PRECEDES_VOTE,
      sequence_flow_rationale:
        "The engagement started in 2021-04-14, while the vote occurred on 2024-05-01. The engagement record also mentions the vote, indicating that the engagement preceded the vote.",
      run_id: "f0e7f9fd964b",
      run_timestamp: "20251126_184121",
    },
    {
      compass_cid: "BHI50548833",
      company_name: "BOOKING HOLDINGS INC",
      voting_record_id:
        "14931832-09_jun_22-jp_morgan-absolute_return_fund-against",
      engagement_record_id:
        "BHI50548833-corporate_issue-basic_shareholder_rights-threshold_for_shareholders_to_call_special_meeting-04_06_2017-jp_morgan",
      confidence_score: 0.8,
      rationale:
        "The engagement record discusses the threshold for shareholders to call a special meeting, and the voting record concerns a proposal to reduce the ownership threshold for shareholders to call a special meeting. Both records are from JP Morgan. The engagement started in 2017, well before the vote in 2022, suggesting a proactive engagement. The vote against the proposal suggests the engagement was not successful in changing the company's stance, or that the manager disagreed with the specific proposal despite the general engagement objective.",
      is_cross_manager: false,
      matching_factors: "shareholder rights|special meeting threshold",
      interaction_class: InteractionClass.INTERNAL_INCONSISTENCY,
      interaction_class_rationale:
        "The same manager (JP Morgan) engaged on shareholder rights but voted against a proposal to reduce the ownership threshold for calling a special meeting, indicating a misalignment between engagement objectives and voting behavior.",
      linkage_type: LinkageType.ESCALATION_PATH,
      linkage_type_rationale:
        "The engagement on shareholder rights preceded the vote on the special meeting threshold, suggesting the vote was a form of escalation after the engagement did not achieve the desired outcome.",
      sequence_flow: SequenceFlow.ENGAGEMENT_PRECEDES_VOTE,
      sequence_flow_rationale:
        "The engagement started in 2017, while the vote occurred in 2022, indicating that the engagement preceded the vote.",
      run_id: "f0e7f9fd964b",
      run_timestamp: "20251126_184121",
    },
    {
      compass_cid: "DPA78255083",
      company_name: "DHL GROUP",
      voting_record_id:
        "14723161-06_may_22-DPA78255083-jp_morgan-absolute_return_fund-for",
      engagement_record_id:
        "DPA78255083-corporate_issue-audit_accounting-auditor_tenure-08_06_2017-jp_morgan",
      confidence_score: 0.8,
      rationale:
        "The J.P. Morgan vote 'For' ratification of PricewaterhouseCoopers GmbH as auditors includes the rationale 'Concerns regarding Auditor tenure'. This aligns with J.P. Morgan's own engagement on 'Auditor tenure' initiated in 2017, indicating a consistent concern. The vote occurred significantly after the engagement began, suggesting a potential influence. The fact that the voting rationale explicitly mentions tenure strengthens the link.",
      is_cross_manager: false,
      matching_factors: "Auditor tenure|Audit-related",
      interaction_class: InteractionClass.CONSISTENT_REINFORCEMENT,
      interaction_class_rationale:
        "The same manager (J.P. Morgan) voted in a way that supports their prior engagement goal regarding auditor tenure.",
      linkage_type: LinkageType.THEMATIC_ALIGNMENT,
      linkage_type_rationale:
        "The vote and engagement share a common ESG goal related to audit practices but are separated by time and focus, with no explicit causal threat made.",
      sequence_flow: SequenceFlow.ENGAGEMENT_PRECEDES_VOTE,
      sequence_flow_rationale:
        "The engagement on auditor tenure started in 2017, preceding the 2022 vote, suggesting the engagement potentially influenced the voting decision.",
      run_id: "f0e7f9fd964b",
      run_timestamp: "20251126_184121",
    },
    {
      compass_cid: "AEC19919737",
      company_name: "AMERICAN EXPRESS CO",
      voting_record_id:
        "14711404-03_may_22-AEC19919737-jp_morgan-absolute_return_fund-against",
      engagement_record_id:
        "AEC19919737-corporate_issue-pay_design_and_transparency-executive_pay-2017_06_30-lgps_central",
      confidence_score: 0.8,
      rationale:
        "The voting record shows a vote AGAINST the advisory vote to ratify named executive officers' compensation, and the rationale indicates an 'apparent failure to link pay and appropriate performance.' This aligns with the engagement record's focus on executive pay, initiated in 2017. The engagement aimed to address executive pay issues. The vote against management recommendation, combined with the rationale, strongly suggests the voting manager's concerns about executive compensation link to the prior engagement. The absence of more recent engagement data weakens the confidence somewhat.",
      is_cross_manager: false,
      matching_factors:
        "executive compensation|pay and performance misalignment",
      interaction_class: InteractionClass.CONSISTENT_REINFORCEMENT,
      interaction_class_rationale:
        "The same manager is involved. The engagement activity highlights concerns about executive pay, and the voting decision reflects similar concerns, indicating consistent reinforcement.",
      linkage_type: LinkageType.THEMATIC_ALIGNMENT,
      linkage_type_rationale:
        "The engagement and voting records both address executive compensation, but there's no direct evidence of escalation. The vote reflects the concerns raised during the engagement, indicating a thematic alignment.",
      sequence_flow: SequenceFlow.ENGAGEMENT_PRECEDES_VOTE,
      sequence_flow_rationale:
        "The engagement started in 2017, well before the 2022 vote, suggesting the engagement preceded the voting decision and potentially influenced it.",
      run_id: "f0e7f9fd964b",
      run_timestamp: "20251126_184121",
    },
    {
      compass_cid: "TTCI47314438",
      company_name: "TJX COMPANIES INC",
      voting_record_id:
        "14948446-07_jun_22-TTCI47314438-rbc-absolute_return_fund-for",
      engagement_record_id:
        "TTCI47314438-corporate_objective-supply_chain_rights-human_rights_disclosure-2024_06_30-lgps_central",
      confidence_score: 0.8,
      rationale:
        "The voting record and engagement record both address human rights in the supply chain. The voting record, dated June 7, 2022, shows a vote for a shareholder proposal requesting a report on assessing due diligence on human rights in the supply chain. The vote rationale states that the proposal promotes better management of ESG opportunities and risks. The engagement started in June 2024, aiming to increase the company's disclosure on human rights. The engagement follows the vote, and the vote supports the engagement's objective. The manager is the same. The engagement objective specifically mentions the process and outcomes of human rights due diligence, which directly relates to the shareholder proposal.",
      is_cross_manager: false,
      matching_factors: "human rights|supply chain|due diligence|disclosure",
      interaction_class: InteractionClass.CONSISTENT_REINFORCEMENT,
      interaction_class_rationale:
        "The same manager voted in favor of a shareholder proposal that aligns with their subsequent engagement objective of increasing the company's disclosure on human rights, particularly regarding due diligence.",
      linkage_type: LinkageType.THEMATIC_ALIGNMENT,
      linkage_type_rationale:
        "The engagement and vote share a common ESG goal (human rights in the supply chain) but are separated by time and focus. The vote reinforces the importance of human rights due diligence, a theme raised in the later engagement.",
      sequence_flow: SequenceFlow.VOTE_PRECEDES_ENGAGEMENT,
      sequence_flow_rationale:
        "The vote occurred in June 2022, while the engagement started in June 2024. The vote may have influenced the later engagement.",
      run_id: "f0e7f9fd964b",
      run_timestamp: "20251126_184121",
    },
  ];

/**
 * Get mock mappings for a specific company
 */
export const getMockMappingsForCompany = (
  companyId: string
): EngagementVotingMappingRecord[] => {
  return MOCK_ENGAGEMENT_VOTING_MAPPINGS.filter(
    (mapping) => mapping.compass_cid === companyId
  );
};

/**
 * Get mock mappings for a specific engagement record
 */
export const getMockMappingsForEngagement = (
  engagementRecordId: string
): EngagementVotingMappingRecord[] => {
  return MOCK_ENGAGEMENT_VOTING_MAPPINGS.filter(
    (mapping) => mapping.engagement_record_id === engagementRecordId
  );
};
